import React, { useEffect, useState } from "react";
import { Loading } from "@/components/LoadingState";
import { Users, Banknote, Receipt, Utensils, CalendarOff, CalendarDays, Download, Plus, Trash2, Edit, Calculator, Building2, Sparkles, Scale, TrendingUp, FileText, Settings as SettingsIcon, X, Award, Shield, Smartphone, Bell, LogOut, KeyRound, User as UserIcon, Clock, RefreshCw, HandCoins, FileBarChart, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { Toaster, toast } from "sonner";
import { api, AYLAR, formatTL, openPdf } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import LoginPage from "@/components/LoginPage";
import PersonnelTab from "@/components/PersonnelTab";
import AdvanceTab from "@/components/AdvanceTab";
import AnnualLeaveTab from "@/components/AnnualLeaveTab";
import PayrollTab from "@/components/PayrollTab";
import PuantajTab from "@/components/PuantajTab";
import PrimTab from "@/components/PrimTab";
import PrimPendingTab from "@/components/PrimPendingTab";
import PrimSummaryTab from "@/components/PrimSummaryTab";
import KidemReserveTab from "@/components/KidemReserveTab";
import BildirimTab from "@/components/BildirimTab";
import UsersTab from "@/components/UsersTab";
import CreditorsTab from "@/components/CreditorsTab";
import TahsilatTab from "@/components/TahsilatTab";
import GiderAvansTab from "@/components/GiderAvansTab";
import ReportsTab from "@/components/ReportsTab";
import PrimRateRulesPanel from "@/components/PrimRateRulesPanel";
import CreditorFlagRulesPanel from "@/components/CreditorFlagRulesPanel";
import GidenScopePositionsPanel from "@/components/GidenScopePositionsPanel";
import PozisyonYonetimPanel from "@/components/PozisyonYonetimPanel";
import YearlyRatesPanel from "@/components/YearlyRatesPanel";
import BackupPanel from "@/components/BackupPanel";
import AutoBackupPanel from "@/components/AutoBackupPanel";
import ScheduledJobsPanel from "@/components/ScheduledJobsPanel";
import { BackupReminderPanel } from "@/components/BackupReminderPanel";
import { AutoBackupAlert } from "@/components/AutoBackupAlert";
import { ChangePasswordModal } from "@/components/ChangePasswordModal";
import { useStickyTableAlign } from "@/lib/useStickyTableAlign";
import { FloatingHScroll } from "@/components/FloatingHScroll";
import { AdvanceRequestButton } from "@/components/advance/AdvanceRequestButton";
import { CarryWarningPanel } from "@/components/advance/CarryWarningPanel";
import ResmiTatilPanel from "@/components/ResmiTatilPanel";
import LegacyMigrationPanel from "@/components/LegacyMigrationPanel";
import CarryoverPanel from "@/components/CarryoverPanel";
import { StaffAlertsBanner } from "@/components/StaffAlertsBanner";
import SecurityHealthCard from "@/components/SecurityHealthCard";
import { setCustomPozisyonColors } from "@/lib/pozisyonColors";
import { KpiDetailModal } from "@/components/payroll/KpiDetailModal";

const now = new Date();

export default function App() {
  const auth = useAuth();
  if (auth.initializing) {
    return <div className="min-h-screen bg-slate-50 grid place-items-center"><Loading text="Sistem açılıyor..." /></div>;
  }
  if (!auth.user) {
    return <><Toaster position="top-right" richColors /><LoginPage /></>;
  }
  if (auth.user.must_change_password && !auth.isImpersonating) {
    return <><Toaster position="top-right" richColors /><ChangePasswordModal forced onClose={() => auth.refresh()} /></>;
  }
  return <AppShell />;
}

function AppShell() {
  const headerRef = useStickyTableAlign();
  const auth = useAuth();
  const defaultTab = (auth.allowedTabs && auth.allowedTabs[0]) || "payroll";
  const [tab, setTab] = useState(defaultTab);
  const [yil, setYil] = useState(now.getFullYear());
  const [ay, setAy] = useState(now.getMonth() + 1);

  // Allow sub-tabs (e.g. Prim Özeti) to update year/month via window events
  useEffect(() => {
    const onY = (e) => setYil(Number(e.detail));
    const onA = (e) => setAy(Number(e.detail));
    const onGotoTab = (e) => {
      const target = e.detail;
      if (target && auth.allowedTabs?.includes(target)) {
        setTab(target);
      }
    };
    window.addEventListener("bordromat:setYil", onY);
    window.addEventListener("bordromat:setAy", onA);
    window.addEventListener("bordromat:gotoTab", onGotoTab);
    return () => {
      window.removeEventListener("bordromat:setYil", onY);
      window.removeEventListener("bordromat:setAy", onA);
      window.removeEventListener("bordromat:gotoTab", onGotoTab);
    };
  }, [auth.allowedTabs]);
  const [dashboard, setDashboard] = useState(null);
  const [prevDashboard, setPrevDashboard] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [kpiDetail, setKpiDetail] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    // Fetch admin-defined pozisyon color overrides so badges render consistently app-wide.
    if (auth.isMuhasebeStj || auth.isJuniorMuhasebe) return;
    api.get("/position-templates/colors")
      .then((r) => setCustomPozisyonColors(r.data?.colors || {}))
      .catch(() => {});
    // When admin updates a pozisyon color, force every mounted component to
    // re-render so the new palette shows up without a full page reload.
    const onColorsUpdated = () => setRefreshKey((k) => k + 1);
    const onSecChanged = () => api.get(`/admin-notifications?unread_only=true&entity_type=export_formula_warning,prim_month_changed&limit=1`)
      .then((r) => setDashboard((d) => ({ ...(d || {}), security_alert_count: r.data?.unread_count || 0 }))).catch(() => {});
    window.addEventListener("bordromat:security-alerts-changed", onSecChanged);
    window.addEventListener("bordromat:pozisyon-colors-updated", onColorsUpdated);
    return () => {
      window.removeEventListener("bordromat:pozisyon-colors-updated", onColorsUpdated);
      window.removeEventListener("bordromat:security-alerts-changed", onSecChanged);
    };
  }, [auth.isMuhasebeStj, auth.isJuniorMuhasebe]);

  useEffect(() => {
    if (auth.isMuhasebeStj || auth.isPersonel || auth.isJuniorMuhasebe) return;  // scoped roles: no dashboard payload
    api.get(`/dashboard?yil=${yil}&ay=${ay}`).then((r) => setDashboard((d) => ({ ...(d || {}), ...r.data }))).catch(() => {});
    const py = ay === 1 ? yil - 1 : yil, pa = ay === 1 ? 12 : ay - 1;
    api.get(`/dashboard?yil=${py}&ay=${pa}`).then((r) => setPrevDashboard(r.data)).catch(() => setPrevDashboard(null));
    // Pending prim count (admin only — endpoint returns 0 for non-admin)
    if (auth.isAdmin) {
      api.get(`/prim/pending/count`)
        .then((r) => setDashboard((d) => ({ ...(d || {}), pending_prim_count: r.data?.count || 0 })))
        .catch(() => {});
      if (auth.isAdmin || auth.isMuhasebeci) api.get(`/advance-requests?status=bekliyor`)
        .then((r) => setDashboard((d) => ({ ...(d || {}), advance_request_count: r.data?.pending || 0 })))
        .catch(() => {});
      api.get(`/admin-notifications?unread_only=true&entity_type=personnel_added&limit=1`)
        .then((r) => setDashboard((d) => ({ ...(d || {}), new_personnel_count: r.data?.unread_count || 0 })))
        .catch(() => {});
      api.get(`/admin-notifications?unread_only=true&entity_type=export_formula_warning,prim_month_changed&limit=1`)
        .then((r) => setDashboard((d) => ({ ...(d || {}), security_alert_count: r.data?.unread_count || 0 })))
        .catch(() => {});
    }
  }, [yil, ay, refreshKey, auth.isAdmin, auth.isMuhasebeci, auth.isMuhasebeStj, auth.isJuniorMuhasebe, auth.isPersonel, tab]);

  const handleExport = async () => {
    try {
      let hiddenParam = "";
      try {
        const raw = localStorage.getItem("payroll.hiddenCols.v2");
        if (raw) {
          const arr = JSON.parse(raw) || [];
          if (arr.length) hiddenParam = `&hidden_cols=${encodeURIComponent(arr.join(","))}`;
        }
      } catch {}
      const res = await api.get(`/payroll/export?yil=${yil}&ay=${ay}${hiddenParam}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `bordro_${yil}_${String(ay).padStart(2, "0")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Excel bordro raporu indirildi");
    } catch (e) {
      toast.error("Excel oluşturulurken hata oluştu");
    }
  };

  const annualPdfUrl = () => {
    let hp = "";
    try {
      const raw = localStorage.getItem("payroll.hiddenCols.v2");
      if (raw) {
        const arr = JSON.parse(raw) || [];
        if (arr.length) hp = `&hidden_cols=${encodeURIComponent(arr.join(","))}`;
      }
    } catch {}
    return `/report/annual?yil=${yil}${hp}`;
  };

  const yilOptions = [];
  for (let y = now.getFullYear() + 1; y >= 2022; y--) yilOptions.push(y);

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      <FloatingHScroll />

      {auth.isImpersonating && <ImpersonationBanner />}

      {/* Header */}
      <header ref={headerRef} className="border-b-2 border-slate-900 bg-white sticky top-0 z-30 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src="/akat-logo.png" alt="Akat Hukuk" className="h-14 w-auto object-contain rounded-lg" data-testid="akat-logo" />
            <div className="h-10 w-px bg-slate-200"></div>
            <div>
              <div className="font-display text-xl font-extrabold text-slate-900 tracking-tight leading-tight">
                BordroMat
              </div>
              <div className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">
                Hukuk Ofisi Muhasebe
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
              <select
                data-testid="month-select"
                value={ay}
                onChange={(e) => setAy(Number(e.target.value))}
                className="bg-white border border-slate-200 rounded-md px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-slate-900"
              >
                {AYLAR.map((m) => <option key={m.v} value={m.v}>{m.n}</option>)}
              </select>
              <select
                data-testid="year-select"
                value={yil}
                onChange={(e) => setYil(Number(e.target.value))}
                className="bg-white border border-slate-200 rounded-md px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-slate-900"
              >
                {yilOptions.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            {auth.isAdmin && (
              <>
                <button
                  data-testid="export-excel-button"
                  onClick={handleExport}
                  className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all shadow-sm hover:shadow"
                >
                  <Download size={16} />
                  Excel İndir
                </button>

                <button
                  data-testid="annual-report-button"
                  onClick={() => openPdf(annualPdfUrl())}
                  className="flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all shadow-sm hover:shadow"
                  title={`${yil} yılı için tüm aylar + personeller için PDF rapor`}
                >
                  <FileText size={16} />
                  Yıllık PDF
                </button>

                <button
                  data-testid="open-settings-button"
                  onClick={() => setSettingsOpen(true)}
                  className="flex items-center justify-center w-10 h-10 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                  title="Ayarlar"
                >
                  <SettingsIcon size={16} />
                </button>
              </>
            )}

            {auth.user?.personnel_id && !auth.isAdmin && <AdvanceRequestButton />}
            <PWAInstallButton />

            <UserMenu />
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-[1600px] mx-auto px-6">
          <div className="flex gap-1 -mb-px">
            {[
              { k: "tahsilat", label: "Tahsilatlar", icon: HandCoins },
              { k: "gider-avans", label: "Gider Avansları", icon: Receipt },
              { k: "payroll", label: "Bordro Cetveli", icon: Calculator },
              { k: "puantaj", label: "Puantaj", icon: CalendarOff },
              { k: "prim", label: "Prim Hesaplama", icon: Scale },
              { k: "prim-pending", label: "Onay Bekleyenler", icon: Clock, badge: dashboard?.pending_prim_count || 0 },
              { k: "prim-summary", label: "Prim Özeti", icon: TrendingUp },
              { k: "kidem-reserve", label: "Tazminat Rezervi", icon: Shield },
              { k: "personnel", label: "Personel", icon: Users, badge: dashboard?.new_personnel_count || 0 },
              { k: "advance", label: "Avans Yönetimi", icon: Receipt, badge: dashboard?.advance_request_count || 0 },
              { k: "annual-leave", label: "Yıllık İzinler", icon: CalendarDays },
              { k: "reports", label: "Raporlar", icon: FileBarChart },
              { k: "bildirim", label: "Bildirimler", icon: Bell, badge: (dashboard?.pending_notifications || 0) + (dashboard?.security_alert_count || 0) },
              { k: "users", label: "Kullanıcılar", icon: UserIcon },
              { k: "creditors", label: "Müvekkiller", icon: Building2 },
            ].filter((t) => auth.canAccess(t.k)).map(({ k, label, icon: Icon, badge }) => (
              <button
                key={k}
                data-testid={`tab-${k}`}
                onClick={() => setTab(k)}
                className={`relative flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  tab === k
                    ? "border-red-700 text-slate-900"
                    : "border-transparent text-slate-500 hover:text-slate-900"
                }`}
              >
                <Icon size={16} />
                {label}
                {badge > 0 && (
                  <span data-testid={`tab-${k}-badge`} className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-600 text-white text-[10px] font-bold">
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* KPI Cards */}
      {auth.canSeeKpiCards && (
      <div className="max-w-[1600px] mx-auto px-6 pt-6">
        <div className={`grid grid-cols-2 ${auth.canSeeMoney ? "md:grid-cols-5" : "md:grid-cols-2"} gap-4`}>
          <KpiCard
            testid="kpi-personnel"
            label="Aktif Personel"
            value={dashboard?.total_personnel ?? 0}
            prev={prevDashboard?.total_personnel}
            icon={Users}
            tint="blue"
            isCount
          />
          {auth.canSeeMoney && (
            <>
              <KpiCard
                onClick={() => setKpiDetail("net")}
                testid="kpi-net-salary"
                label="Toplam Net Maaş"
                value={dashboard?.total_net_maas ?? 0}
            prev={prevDashboard?.total_net_maas}
                icon={Banknote}
                tint="emerald"
              />
              <KpiCard
                onClick={() => setKpiDetail("avans")}
                testid="kpi-advance"
                label="Avans Kesintisi"
                value={dashboard?.total_avans_kesinti ?? 0}
            prev={prevDashboard?.total_avans_kesinti}
                icon={Receipt}
                tint="amber"
              />
              <KpiCard
                testid="kpi-food"
                label="Yemek Toplam"
                value={dashboard?.total_yemek ?? 0}
            prev={prevDashboard?.total_yemek}
                icon={Utensils}
                tint="indigo"
                onClick={() => setKpiDetail("yemek")}
              />
            </>
          )}
          <KpiCard
            testid="kpi-leaves"
            label="İzin + Rapor Günü"
            value={(dashboard?.total_izin_gun ?? 0) + (dashboard?.total_rapor_gun ?? 0)}
            prev={prevDashboard ? (prevDashboard.total_izin_gun ?? 0) + (prevDashboard.total_rapor_gun ?? 0) : undefined}
            icon={CalendarOff}
            tint="rose"
            isCount
            onClick={() => setKpiDetail("izin")}
          />
        </div>
      </div>
      )}

      {auth.isAdmin && <AutoBackupAlert />}
      {auth.isAdmin && <BackupReminderPanel />}
      {auth.canSeeMoney && (auth.isAdmin || auth.isMuhasebeci) && (
        <CarryWarningPanel yil={yil} ay={ay} refreshKey={refreshKey} onOpen={() => setTab("advance")} />
      )}

      {/* Content */}
      {(auth.isAdmin || auth.isMuhasebeci) && !auth.isImpersonating && <StaffAlertsBanner />}
      <main className="max-w-[1600px] mx-auto px-6 py-6">
        {tab === "payroll" && <PayrollTab yil={yil} ay={ay} onChange={refresh} refreshKey={refreshKey} />}
        {tab === "puantaj" && <PuantajTab yil={yil} ay={ay} onChange={refresh} refreshKey={refreshKey} />}
        {tab === "prim" && <PrimTab yil={yil} ay={ay} />}
        {tab === "prim-pending" && <PrimPendingTab />}
        {tab === "prim-summary" && <PrimSummaryTab yil={yil} ay={ay} />}
        {tab === "kidem-reserve" && <KidemReserveTab />}
        {tab === "personnel" && <PersonnelTab yil={yil} ay={ay} onChange={refresh} refreshKey={refreshKey} />}
        {tab === "advance" && <AdvanceTab yil={yil} ay={ay} onChange={refresh} refreshKey={refreshKey} />}
        {tab === "annual-leave" && <AnnualLeaveTab />}
        {tab === "bildirim" && <BildirimTab />}
        {tab === "users" && <UsersTab />}
        {tab === "creditors" && <CreditorsTab />}
        {tab === "tahsilat" && <TahsilatTab />}
        {tab === "gider-avans" && <GiderAvansTab />}
        {tab === "reports" && <ReportsTab />}
      </main>

      <footer className="max-w-[1600px] mx-auto px-6 py-6 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
        <Building2 size={12} />
        AKAT HUKUK & DANIŞMANLIK · BordroMat · Hukuk Ofisi Bordro Yönetim Sistemi
        <Sparkles size={12} />
      </footer>

      {kpiDetail && <KpiDetailModal kind={kpiDetail} yil={yil} ay={ay} onClose={() => setKpiDetail(null)} />}
      {settingsOpen && (
        <SettingsModal onClose={() => setSettingsOpen(false)} onSaved={() => { setSettingsOpen(false); refresh(); }} />
      )}
    </div>
  );
}

function SettingsModal({ onClose, onSaved }) {
  const auth = useAuth();
  const [kidemOran, setKidemOran] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setKidemOran(String(r.data.kidem_oran_yillik_yuzde ?? 2));
      setLoading(false);
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post("/settings", { kidem_oran_yillik_yuzde: Number(kidemOran) });
      toast.success("Ayarlar güncellendi · Bordrolar yeniden hesaplanacak");
      onSaved();
    } catch {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-3xl my-6" data-testid="settings-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white rounded-t-xl">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <SettingsIcon size={18} /> Bordro Ayarları
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Award size={16} className="text-emerald-700" />
              <h4 className="font-semibold text-sm">Kıdem Primi Oranı</h4>
            </div>
            <p className="text-xs text-slate-600 mb-3">
              Her tam yıl kıdem için personelin aylık brüt ücretinin yüzde kaçının kıdem primi olarak bordroya yansıtılacağını belirler.
              Formül: <code className="bg-slate-200 px-1 rounded">brüt × oran × tam_yıl</code>
            </p>
            <div className="flex items-center gap-2">
              <input
                data-testid="kidem-oran-input"
                type="number"
                step="0.01"
                min="0"
                value={kidemOran}
                disabled={loading}
                onChange={(e) => setKidemOran(e.target.value)}
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono-tab text-right focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
              <span className="text-sm font-semibold text-slate-700">% / yıl</span>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Önerilen değer: <strong>2.00%</strong>. 0 yaparsanız kıdem primi hesaplanmaz.
            </p>
          </div>

          {auth.isAdmin && <SecurityHealthCard />}
          {auth.isAdmin && <><BackupPanel /><AutoBackupPanel /><ScheduledJobsPanel /></>}
          {auth.isAdmin && <YearlyRatesPanel onChanged={onSaved} />}
          {auth.isAdmin && <PozisyonYonetimPanel onChanged={onSaved} />}
          {auth.isAdmin && <ResmiTatilPanel />}
          {auth.isAdmin && <CarryoverPanel />}
          {auth.isAdmin && <LegacyMigrationPanel />}
          {auth.isAdmin && <PrimRateRulesPanel />}
          {auth.isAdmin && <CreditorFlagRulesPanel />}
          {auth.isAdmin && <GidenScopePositionsPanel />}

          <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white pb-0">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold">İptal</button>
            <button
              data-testid="save-settings-button"
              onClick={handleSave}
              disabled={saving || loading}
              className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50"
            >
              {saving ? "Kaydediliyor..." : "Kıdem Oranını Kaydet"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const tintMap = {
  blue: { bg: "bg-blue-50", text: "text-blue-700", icon: "bg-blue-600" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", icon: "bg-emerald-600" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", icon: "bg-amber-600" },
  indigo: { bg: "bg-indigo-50", text: "text-indigo-700", icon: "bg-indigo-600" },
  rose: { bg: "bg-rose-50", text: "text-rose-700", icon: "bg-rose-600" },
};

function KpiCompare({ value, prev, isCount, testid }) {
  if (prev === undefined || prev === null) return null;
  const pct = prev ? ((value - prev) / prev) * 100 : null;
  const fmt = (v) => (isCount ? Number(v).toLocaleString("tr-TR", { maximumFractionDigits: 1 }) : formatTL(v));
  const flat = pct !== null && Math.abs(pct) < 0.05;
  return (
    <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1 flex-wrap" data-testid={`${testid}-compare`}>
      <span>Geçen ay: {fmt(prev)}</span>
      {pct === null ? (
        value > 0 && <span className="font-semibold text-slate-600">· yeni</span>
      ) : flat ? (
        <span className="font-semibold text-slate-600">· değişmedi</span>
      ) : (
        <span className={`inline-flex items-center font-semibold ${pct > 0 ? "text-emerald-700" : "text-rose-700"}`}>
          {pct > 0 ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
          %{Math.abs(pct).toLocaleString("tr-TR", { maximumFractionDigits: 1, minimumFractionDigits: 1 })}
        </span>
      )}
    </div>
  );
}

function KpiCard({ label, value, prev, icon: Icon, tint, isCount, testid, hint, onClick }) {
  const t = tintMap[tint];
  return (
    <div data-testid={testid} onClick={onClick} role={onClick ? "button" : undefined}
      title={onClick ? "Detayı görmek için tıklayın" : undefined}
      className={`bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden ${onClick ? "cursor-pointer hover:border-slate-400" : ""}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
            {label}
          </div>
          <div className={`font-mono-tab text-2xl font-bold ${t.text}`}>
            {isCount ? value : formatTL(value)}
          </div>
          <KpiCompare value={value} prev={prev} isCount={isCount} testid={testid} />
          {hint && (
            <div className="text-[10px] text-slate-500 mt-1 font-medium" data-testid={testid ? `${testid}-hint` : undefined}>
              {hint}
            </div>
          )}
        </div>
        <div className={`${t.icon} text-white w-9 h-9 rounded-lg grid place-items-center`}>
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}

function PWAInstallButton() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(
    typeof window !== "undefined" && window.matchMedia && window.matchMedia("(display-mode: standalone)").matches
  );
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const installedHandler = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", installedHandler);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  if (installed) return null;
  // Hide if neither prompt available nor iOS (so Android desktop before prompt won't show button)
  if (!deferred && !isIOS) return null;

  const handleClick = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferred(null);
    } else if (isIOS) {
      setIosHint(true);
    }
  };

  return (
    <>
      <button
        data-testid="pwa-install-button"
        onClick={handleClick}
        className="hidden sm:flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg font-semibold text-xs transition-colors shadow-sm"
        title="Telefon ana ekranına ekle"
      >
        <Smartphone size={14} />
        Ana Ekrana Ekle
      </button>
      <button
        data-testid="pwa-install-button-mobile"
        onClick={handleClick}
        className="sm:hidden flex items-center justify-center w-10 h-10 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors shadow-sm"
        title="Ana ekrana ekle"
      >
        <Smartphone size={16} />
      </button>
      {iosHint && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => setIosHint(false)}>
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()} data-testid="pwa-ios-hint">
            <div className="flex items-start justify-between gap-3 mb-3">
              <h3 className="font-display text-lg font-bold flex items-center gap-2">
                <Smartphone size={18} /> Ana Ekrana Ekle
              </h3>
              <button onClick={() => setIosHint(false)} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center">
                <X size={16} />
              </button>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-slate-700">
              <li>Safari'nin alt barındaki <strong>Paylaş</strong> butonuna dokunun</li>
              <li>Açılan menüden <strong>"Ana Ekrana Ekle"</strong> seçeneğini bulun</li>
              <li>Sağ üstteki <strong>"Ekle"</strong> butonuna dokunun — BordroMat ana ekranda olacak</li>
            </ol>
            <button
              onClick={() => setIosHint(false)}
              className="w-full mt-4 bg-slate-900 text-white px-4 py-2 rounded-lg font-semibold text-sm"
            >
              Anladım
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function UserMenu() {
  const { user, logout, impersonatedBy } = useAuth();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const k = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open]);
  if (!user) return null;
  const initials = (user.name || user.email || "?").slice(0, 2).toUpperCase();
  const roleLabel = user.role === "admin" ? "Admin" : user.role === "yonetici" ? "Yönetici" : user.role === "muhasebeci" ? "Muhasebeci" : user.role === "muhasebe_stj" ? "Muhasebe Stj." : user.role === "junior_muhasebe" ? "Junior Muhasebe" : "Personel";
  const roleTone = user.role === "admin" ? "bg-red-100 text-red-700" : user.role === "yonetici" ? "bg-violet-100 text-violet-700" : user.role === "muhasebeci" ? "bg-amber-100 text-amber-700" : user.role === "muhasebe_stj" ? "bg-teal-100 text-teal-700" : user.role === "junior_muhasebe" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700";
  return (
    <div className="relative">
      <button
        data-testid="user-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900 pl-1 pr-3 py-1 rounded-full transition-colors"
      >
        <span className="w-8 h-8 rounded-full bg-slate-900 text-white font-bold text-xs grid place-items-center">{initials}</span>
        <span className="hidden sm:flex flex-col items-start">
          <span className="text-xs font-semibold leading-tight">{user.name || user.email.split("@")[0]}</span>
          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 rounded-full ${roleTone}`}>{roleLabel}</span>
        </span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}></div>
          <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden" data-testid="user-menu-content">
            <div className="p-3 border-b border-slate-100">
              <div className="font-semibold text-sm text-slate-900 truncate">{user.name || "—"}</div>
              <div className="text-xs text-slate-500 truncate">{user.email}</div>
              <span className={`inline-flex mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${roleTone}`}>{roleLabel}</span>
            </div>
            {!impersonatedBy && (
              <button data-testid="change-password-button" onClick={() => { setOpen(false); setPwOpen(true); }}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 font-semibold border-b border-slate-100">
                <KeyRound size={14} /> Şifre Değiştir
              </button>
            )}
            <button
              data-testid="logout-button"
              onClick={async () => { setOpen(false); await logout(); toast.success("Çıkış yapıldı"); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-rose-600 hover:bg-rose-50 font-semibold"
            >
              <LogOut size={14} /> Çıkış Yap
            </button>
          </div>
        </>
      )}
      {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}
    </div>
  );
}

export { Plus, Trash2, Edit };

function ImpersonationBanner() {
  const { user, impersonatedBy, impersonationExpiresAt, stopImpersonation, extendImpersonation } = useAuth();
  const [busy, setBusy] = useState(false);
  const [extending, setExtending] = useState(false);
  const [remainingMs, setRemainingMs] = useState(() => computeRemainingMs(impersonationExpiresAt));
  const warnedRef = React.useRef(false);
  const expiredRef = React.useRef(false);

  useEffect(() => {
    setRemainingMs(computeRemainingMs(impersonationExpiresAt));
    warnedRef.current = false;
    expiredRef.current = false;
  }, [impersonationExpiresAt]);

  const handleStop = React.useCallback(async (auto = false) => {
    setBusy(true);
    try {
      await stopImpersonation();
      toast.success(
        auto
          ? "Impersonation süresi doldu, yönetici hesabına geri dönüldü"
          : `${impersonatedBy?.email || "Yönetici"} hesabına geri döndünüz`
      );
    } catch (e) {
      toast.error("Çıkış yapılamadı");
    } finally {
      setBusy(false);
    }
  }, [stopImpersonation, impersonatedBy]);

  useEffect(() => {
    if (!impersonationExpiresAt) return;
    const tick = () => {
      const ms = computeRemainingMs(impersonationExpiresAt);
      setRemainingMs(ms);
      if (ms > 0 && ms <= 2 * 60 * 1000 && !warnedRef.current) {
        warnedRef.current = true;
        toast.warning("Impersonation 2 dakika içinde sona erecek", { duration: 6000 });
      }
      if (ms <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        handleStop(true);
      }
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [impersonationExpiresAt, handleStop]);

  const handleExtend = async () => {
    setExtending(true);
    try {
      await extendImpersonation();
      toast.success("Impersonation süresi 30 dakika uzatıldı");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Süre uzatılamadı");
    } finally {
      setExtending(false);
    }
  };

  const mmss = formatMmSs(remainingMs);
  const danger = remainingMs > 0 && remainingMs <= 2 * 60 * 1000;

  return (
    <div
      data-testid="impersonation-banner"
      className={`w-full ${danger ? "bg-rose-700 animate-pulse" : "bg-rose-600"} text-white px-4 py-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs sm:text-sm font-semibold shadow-md sticky top-0 z-40`}
    >
      <span className="inline-flex items-center gap-2">
        <Shield size={14} />
        <span>
          <span className="opacity-80">Impersonation:</span>{" "}
          <strong>{impersonatedBy?.email}</strong> olarak{" "}
          <strong>{user?.email}</strong> hesabına giriş yaptınız
        </span>
      </span>
      <span
        data-testid="impersonation-countdown"
        className={`inline-flex items-center gap-1.5 bg-white/15 border border-white/30 rounded-md px-2 py-0.5 font-mono-tab tracking-widest ${danger ? "text-white" : ""}`}
        title="Kalan süre"
      >
        <Clock size={12} /> {mmss}
      </span>
      <button
        data-testid="extend-impersonation-button"
        onClick={handleExtend}
        disabled={extending}
        className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 border border-white/30 rounded-md px-3 py-1 font-bold uppercase tracking-wider text-[11px] disabled:opacity-50"
        title="Süreyi 30 dakika uzat"
      >
        <RefreshCw size={12} /> {extending ? "Uzatılıyor..." : "Süreyi Uzat"}
      </button>
      <button
        data-testid="stop-impersonation-button"
        onClick={() => handleStop(false)}
        disabled={busy}
        className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 border border-white/30 rounded-md px-3 py-1 font-bold uppercase tracking-wider text-[11px] disabled:opacity-50"
      >
        <LogOut size={12} /> {busy ? "Çıkılıyor..." : "Çık"}
      </button>
    </div>
  );
}

function computeRemainingMs(iso) {
  if (!iso) return 0;
  try {
    return Math.max(0, new Date(iso).getTime() - Date.now());
  } catch { return 0; }
}

function formatMmSs(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
